import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSocket } from "./SocketProvider";
import { getUser } from "../../services/users";
import auth from '../../services/authservice';
import CallModal from "../../pages/messenger/Chats/CallModal";
import { useStopwatch } from 'react-timer-hook';
import { updateMessages } from "../messengerSlice";
import { useDispatch, useSelector } from "react-redux";
import * as mediasoupClient from "mediasoup-client";

export const CallContext = createContext();

export function useCallContext() {
  return useContext(CallContext);
}

function CallProvider({ children }) {
  const { seconds, minutes, start, pause, reset } = useStopwatch({ autoStart: false });
  const { socket, me } = useSocket();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const messenger = useSelector(state => state.messenger);

  // State variables
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [fromSocket, setFromSocket] = useState(null);
  const [fromProducer, setFromProducer] = useState(null);
  const [caller, setCaller] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [convId, setConvId] = useState(null);
  const [isVideoTrack, setIsVideoTrack] = useState(true);
  const [remoteU, setRemoteU] = useState(null);
  const [remoteUser, setRemoteUser] = useState(null)
  const [isCallModal, setIsCallModal] = useState(false);
  const [isIncomingCall, setIsIncomingCall] = useState(false);

  // Refs
  const isVideo = useRef(false);
  //const remoteUser = useRef(null);
  const conIdRef = useRef();
  const meRef = useRef();
  const remoteURef = useRef();
  const callAnsweredRef = useRef(false);
  const isCallActive = useRef(false);
  const localStreamRef = useRef(null); 
  const remoteStreamRef = useRef(null);
  const endCallCalled = useRef(false);

  // Refs for call data 
  const fromSocketRef = useRef(null);
  const fromProducerRef = useRef(null);

  // Mediasoup refs
  const mediasoupDevice = useRef(null);
  const sendTransport = useRef(null);
  const recvTransport = useRef(null);
  //const producer = useRef(null);
  const producers = useRef({ audio: null, video: null });
  const consumers = useRef(new Map());
  const callTimeoutRef = useRef(null);

  // Sync refs with state
  useEffect(() => {
    conIdRef.current = convId;
    //remoteURef.current = remoteU;
    remoteURef.current = remoteUser;
    meRef.current = me;
  }, [convId, remoteU, me]);

  // Initialize Mediasoup device
  const initializeDevice = useCallback(async () => {
    try {
      const routerRtpCapabilities = await socket.emitWithAck('getRouterRtpCapabilities');
      mediasoupDevice.current = new mediasoupClient.Device();
      await mediasoupDevice.current.load({ routerRtpCapabilities });
      console.log("initializeDevice"); 
    } catch (error) {
      toast.error('Failed to initialize media device');
      throw error;
    }
  }, [socket]);

  // Transport creation
  const createTransport = useCallback(async (direction) => {
    try {
      const { transport: transportOptions } = await socket.emitWithAck(
        direction === 'send' ? 'createProducerTransport' : 'createConsumerTransport',
        { direction, socketId: socket.id }
      );

      const transport = direction === 'send' 
        ? mediasoupDevice.current.createSendTransport(transportOptions)
        : mediasoupDevice.current.createRecvTransport(transportOptions);

            // Add connection state handler


    transport.on('connectionstatechange', (state) => {
      console.log(`Transport state: ${state}`);
      console.log(`Transport ${transport.id} (${direction}) state:`, state);
      if (state === 'connected') {
        console.log('ICE connected, DTLS established');
      }
      if (state === 'failed') {
        transport.close();
      }
    });

      // Add transport creation log
      console.log('Transport created:', transport.id, direction);

      transport.on('connect', async ({ dtlsParameters }, callback, errback) => {
        try {
          await socket.emitWithAck('connectTransport', {
            transportId: transport.id,
            dtlsParameters,
            direction
          });
          callback();
        } catch (error) {
          errback(error);
        }
      });

      if (direction === 'send') {
        transport.on('produce', async (parameters, callback, errback) => {
          try {
            const { id } = await socket.emitWithAck('transport-produce', {
              transportId: transport.id,
              kind: parameters.kind,
              rtpParameters: parameters.rtpParameters,
            });
            callback({ id });
          } catch (error) {
            errback(error);
          }
        });
      }

      return transport;
    } catch (error) {
      toast.error('Failed to create media transport');
      throw error;
    }
  }, [socket]);

  // Handle incoming call
  const handleIncomingCall = useCallback(async (data) => {
    try {
      setIsIncomingCall(true);  // Mark as incoming call
      const { fromUser, isVideoCall, audioProducerId, videoProducerId, fromSocket } = data;
      console.log("i get call", data);
      isVideo.current = isVideoCall;
      // not sure about this
      //setRemoteU(fromUser);
      //remoteUser.current = fromUser;
      //
       if (!fromSocket) {
        console.error('No socket ID in incoming call data', data);
        return;
      }
      fromSocketRef.current = fromSocket;
      setFromSocket(fromSocket);
          // Store both producer IDs
      const producerInfo = {
      audioProducerId,
      videoProducerId
    };
      setFromProducer(producerInfo);
      fromProducerRef.current = producerInfo;
      const userResponse = await getUser(fromUser._id);
      if (!userResponse.data) throw new Error('Failed to fetch caller info');
      //remoteUser.current = userResponse.data;
      setRemoteUser(userResponse.data); // 
      setRemoteU(userResponse.data);
      console.log("remoteUser in handle incoming call",remoteUser);

      setIsCallModal(true);
    } catch (error) {
      console.error('Incoming call error:', error);
      toast.error(error.message || 'Failed to handle incoming call');
    }
  }, [socket]);

  // End call
  // const closeCall = useCallback(() => {
  //   setIsIncomingCall(false);
  //   isCallActive.current = false;
  //   socket.emit('endCall', { 
  //     convId: conIdRef.current, 
  //     //userId: remoteURef.current?._id,
  //     userId: remoteUser?.id,
  //     socketId: fromSocket,
  //   });

  //   //socket?.emit("end call", fromSocket);

  //   //localStream?.getTracks().forEach(track => track.stop());
  //   //remoteStream?.getTracks().forEach(track => track.stop());
  //   localStreamRef.current?.getTracks().forEach(track => track.stop());
  //   remoteStreamRef.current?.getTracks().forEach(track => track.stop());
    
  //   setLocalStream(null);
  //   setRemoteStream(null);
  //   setCaller(false);
  //   setConvId(null);
  //   setRemoteU(null);
  //   setRemoteUser(null);
  //   setFromSocket(null);
  //   setIsCallModal(false);
  //   pause();
  //   reset();

  //   sendTransport.current?.close();
  //   recvTransport.current?.close();
  //   producer.current?.close();
  //   consumers.current.forEach(consumer => consumer.close());
  //   consumers.current.clear();

  //   callAnsweredRef.current = false;
  //   navigate("/messenger/chats");
  // //}, [socket, navigate, pause, reset, localStream, remoteStream, remoteUser,fromSocket]);
  // }, [socket, navigate, pause, reset,remoteUser,fromSocket]);
  const closeCall = useCallback(() => {

    pause();

       if (caller) {
         const messageObj = {
           sender: me?._id,
           //need to be fixed
           receiver: remoteUser?._id,
           message: "",
           media: [],
           conversationId: convId,
           updatedAt: new Date().toLocaleString(),
           createdAt: new Date().toLocaleString(),
           callInfo: {
             isCall: true,
             callType: isVideo ? "Video" : "Audio",
             duration: minutes + ":" + seconds,
             time: new Date().toLocaleString(),
           },
         };
   
         console.log("end with", messageObj, updateMessages);
         // updateMessages(messageObj);
         dispatch(
           updateMessages({
             key: messenger.activeUser,
             message: messageObj,
           })
         );
         socket?.emit("sendMessage", messageObj);
   
       }





    // Prevent multiple calls
    if (endCallCalled.current) return;
    endCallCalled.current = true;
  
    // Notify the server to end call for both parties
    socket.emit('endCall', { 
      targetSocketId: fromSocketRef.current, // The remote participant's socket ID
      callerSocketId: socket.id   // Our own socket ID
    });
  
    // Clean local media
    localStreamRef.current?.getTracks().forEach(track => track.stop());
    remoteStreamRef.current?.getTracks().forEach(track => track.stop());
  
    // Reset all state
    setLocalStream(null);
    setRemoteStream(null);
    setCaller(false);
    setConvId(null);
    setRemoteUser(null);
    setFromSocket(null);
    fromSocketRef.current = null;
    fromProducerRef.current = null;
    setIsCallModal(false);
    pause();
    reset();
  
    // Close Mediasoup resources
    sendTransport.current?.close();
    recvTransport.current?.close();
     // Close all producers
  if (producers.current.audio) producers.current.audio.close();
  if (producers.current.video) producers.current.video.close();
  producers.current = { audio: null, video: null };
  consumers.current.forEach(consumer => consumer.close());
  consumers.current.clear();

  
    // Reset flags
    isCallActive.current = false;
    callAnsweredRef.current = false;
    endCallCalled.current = false;

    
  
    navigate("/messenger/chats");
  }, [socket, navigate, pause, reset, fromSocket]);


  // Initiate call
  const callUser = useCallback(async (userToCall, video, fromUser, convId) => {
    setIsIncomingCall(false);  // Mark as outgoing call
    const user = auth.getProfile();	
    if(user._id == userToCall) return;
  console.log("fromUser",fromUser);
  if (isCallActive.current) {
    toast.error('Already in a call');
    return;
  }
  reset();  // Reset stopwatch
  start();  // Start timing
    try {
      isCallActive.current = true;
      setCaller(true);
      setConvId(convId);
      isVideo.current = video;
      callAnsweredRef.current = false;

      if (!mediasoupDevice.current?.loaded) await initializeDevice();
      sendTransport.current = await createTransport('send');

      const newStream = await navigator.mediaDevices.getUserMedia({
        video: isVideo.current,
        audio: true,
      });
      setLocalStream(newStream);
      localStreamRef.current = newStream;
      console.log("newStream",newStream);

      // const track = isVideo.current 
      //   ? newStream.getVideoTracks()[0]
      //   : newStream.getAudioTracks()[0];

        // Produce AUDIO track (always)
    const audioTrack = newStream.getAudioTracks()[0];
    producers.current.audio = await sendTransport.current.produce({
      track: audioTrack,
      codecOptions: {
        opusStereo: true,
        opusDtx: true
      }
    });
      

      // producer.current = await sendTransport.current.produce({
      //   track,
      //   //encodings: isVideo.current ? [{ scalabilityMode: 'S3T3' }] : undefined,
      //   encodings: isVideo.current ? [
      //     { scalabilityMode: 'L3T3', dtx: true },
      //     { scalabilityMode: 'L2T3', dtx: true, scaleResolutionDownBy: 2 },
      //     { scalabilityMode: 'L1T3', dtx: true, scaleResolutionDownBy: 4 }
      //   ] : undefined,
      //   codecOptions: isVideo.current ? {
      //     videoGoogleStartBitrate: 1000
      //   } : {
      //     opusStereo: true,
      //     opusDtx: true
      //   }
      // });

       // Produce VIDEO track only for video calls
    if (isVideo.current) {
      const videoTrack = newStream.getVideoTracks()[0];
      producers.current.video = await sendTransport.current.produce({
        track: videoTrack,
        encodings: [
          { scalabilityMode: 'L3T3', dtx: true },
          { scalabilityMode: 'L2T3', dtx: true, scaleResolutionDownBy: 2 },
          { scalabilityMode: 'L1T3', dtx: true, scaleResolutionDownBy: 4 }
        ],
        codecOptions: {
          videoGoogleStartBitrate: 1000
        }
      });
    }



      //console.log('Outgoing track produced:', producer.current.id);  // Add here
     
      console.log('Producers created - Audio:', producers.current.audio?.id, 'Video:', producers.current.video?.id);


      callTimeoutRef.current = setTimeout(() => {
        if (!callAnsweredRef.current) {
          toast.error('Call not answered');
          closeCall();
        }
      }, 300000);

      // socket.emit('callUser', {
      //   targetUserId: userToCall._id,
      //   isVideoCall: video,
      //   producerId: producer.current.id,
      //   rtpCapabilities: mediasoupDevice.current.rtpCapabilities,
      //   socketId: socket.id,
      //   fromUser: fromUser,
      // });
      console.log('Initiating call to:', userToCall._id);
      // will coment this part for now
      // await socket.emitWithAck('callUser', {
      //   targetUserId: userToCall._id,
      //   isVideoCall: video,
      //   producerId: producer.current.id,
      //   rtpCapabilities: mediasoupDevice.current.rtpCapabilities,
      //   fromSocket: socket.id,
      //   fromUser: fromUser
      // });
      ////////////

      if (userToCall.isGroupChat) {
        for (const member of userToCall.members) {
         await socket.emitWithAck('callUser', {
          targetUserId: member._id,
          isVideoCall: video,
          //producerId: producer.current.id,
          audioProducerId: producers.current.audio.id,
          videoProducerId: producers.current.video?.id, // null for audio calls
          rtpCapabilities: mediasoupDevice.current.rtpCapabilities,
          fromSocket: socket.id,
          fromUser: fromUser
       });
        }
      } else {
          await socket.emitWithAck('callUser', {
          targetUserId: userToCall._id,
          isVideoCall: video,
          //producerId: producer.current.id,
          audioProducerId: producers.current.audio.id,
          videoProducerId: producers.current.video?.id,
          rtpCapabilities: mediasoupDevice.current.rtpCapabilities,
          fromSocket: socket.id,
          fromUser: fromUser
          });
      }








      

      //remoteUser.current = userToCall;
      setRemoteU(userToCall);
      setRemoteUser(userToCall);

    } catch (error) {
      isCallActive.current = false;
      console.error('Call initiation error:', error);
      toast.error(error.message || 'Failed to initiate call');
      closeCall();
    }
  }, [initializeDevice, createTransport, socket, closeCall]);

  // Handle call acceptance
  useEffect(() => {

    const handleCallAccepted = async (data) => {
      //if (!caller || !isCallActive.current) return;
      console.log("data accept",data);
      try {
        console.log('Received callAccepted with remote audio producerId:', data.audioProducerId);
        console.log('Received callAccepted with remote video producerId:', data.videoProducerId);
        clearTimeout(callTimeoutRef.current);
        callAnsweredRef.current = true;
    // 1. Create server-side consumer
    // const consumerData = await socket.emitWithAck('createConsumer', {
    //   producerId,
    //   rtpCapabilities: mediasoupDevice.current.rtpCapabilities,
    //   socketId
    // });
      // 2. Create local consumer transport
      recvTransport.current = await createTransport('recv');
      const remoteStream = new MediaStream();

    // const consumerData = await socket.emitWithAck('createConsumer', {
    //   producerId: data.fromProducerId,
    //   rtpCapabilities: mediasoupDevice.current.rtpCapabilities,
    //   socketId: data.fromSocket
    // });
      
    // // 3. Create local consumer
    // const consumer = await recvTransport.current.consume({
    //   id: consumerData.id,
    //   producerId: consumerData.producerId,
    //   kind: consumerData.kind,
    //   rtpParameters: consumerData.rtpParameters,
    //   //paused: false
    //   paused: true
    // });
        
     
    // await socket.emitWithAck('resume', {
    //   producerId: data.fromProducerId,
    //   socketId: data.fromSocket
    // });

 // Consume remote AUDIO
      const audioConsumerData = await socket.emitWithAck('createConsumer', {
        producerId: data.audioProducerId,
        rtpCapabilities: mediasoupDevice.current.rtpCapabilities,
        socketId: data.fromSocket
      });

      const audioConsumer = await recvTransport.current.consume({
        id: audioConsumerData.id,
        producerId: audioConsumerData.producerId,
        kind: audioConsumerData.kind,
        rtpParameters: audioConsumerData.rtpParameters,
        paused: false
      });

      await socket.emitWithAck('resume', {
        producerId: data.audioProducerId,
        socketId: data.fromSocket
      });
    setFromSocket(data.fromSocket);
      
        // Consume remote VIDEO if it exists
      let videoConsumer = null;
      if (data.videoProducerId) {
        const videoConsumerData = await socket.emitWithAck('createConsumer', {
          producerId: data.videoProducerId,
          rtpCapabilities: mediasoupDevice.current.rtpCapabilities,
          socketId: data.fromSocket
        });

        videoConsumer = await recvTransport.current.consume({
          id: videoConsumerData.id,
          producerId: videoConsumerData.producerId,
          kind: videoConsumerData.kind,
          rtpParameters: videoConsumerData.rtpParameters,
          paused: false
        });

        await socket.emitWithAck('resume', {
          producerId: data.videoProducerId,
          socketId: data.fromSocket
        });
      }
    
        //consumers.current.set(producerId, consumer);
        //setRemoteStream(new MediaStream([consumer.track]));
        //navigate("/messenger/videocall");

     
      // Add event handlers
      audioConsumer.on('transportclose', () => closeCall());
      audioConsumer.on('trackended', () => closeCall());
      
      if (videoConsumer) {
        videoConsumer.on('transportclose', () => closeCall());
        videoConsumer.on('trackended', () => closeCall());
      }

      // Add tracks to remote stream
      consumers.current.set(audioConsumer.id, audioConsumer);
      remoteStream.addTrack(audioConsumer.track);
      
      if (videoConsumer) {
        consumers.current.set(videoConsumer.id, videoConsumer);
        remoteStream.addTrack(videoConsumer.track);
      }

      setRemoteStream(remoteStream);
      remoteStreamRef.current = remoteStream;

    //setRemoteStream(new MediaStream([consumer.track]));
    
    //navigate("/messenger/videocall");
    navigate("/messenger/videocall", { replace: true });

      } catch (error) {
        console.error('Call acceptance error:', error);
        closeCall();
      }
    };
  


    socket?.on('callAccepted', handleCallAccepted);
    //socket?.on("endCall", () => closeCall);
    return () => socket?.off('callAccepted', handleCallAccepted);
  }, [caller, createTransport, navigate, closeCall, socket]);


    // Answer call
    const answerCall = useCallback(async () => {
      try {
        reset();  // Reset stopwatch
        start();  // Start timing
        // Use refs to avoid stale state values
        const targetSocket = fromSocketRef.current;
        const producerInfo = fromProducerRef.current;
        if (!isCallActive.current) isCallActive.current = true;
        
       console.log('Answering call from socket:', targetSocket);
       if (!producerInfo || !producerInfo.audioProducerId || !targetSocket) {
        throw new Error('Missing call information');
       }
    
       // 0. Ensure device is initialized first
       if (!mediasoupDevice.current?.loaded) {
        console.log('Initializing device...');
        await initializeDevice();
      }
  // 1. Create RECEIVE transport first for consumer
  console.log('Creating receive transport...');
  recvTransport.current = await createTransport('recv');

  // 2. Create SEND transport for producer
  console.log('Creating send transport...');
  sendTransport.current = await createTransport('send');

     // 3. Get local media stream
    console.log('Requesting media permissions...');
    const newStream = await navigator.mediaDevices.getUserMedia({
      video: isVideo.current,
      audio: true,
    });
    setLocalStream(newStream);
    localStreamRef.current = newStream;
    

     // 4. Produce local track
     console.log('Starting local track production...');
  //    const track = isVideo.current 
  //      ? newStream.getVideoTracks()[0]
  //      : newStream.getAudioTracks()[0];
     
  //    //producer.current = await sendTransport.current.produce({ track });
  //        producer.current = await sendTransport.current.produce({
  //       track,
  //       //encodings: isVideo.current ? [{ scalabilityMode: 'S3T3' }] : undefined,
  //       encodings: isVideo.current ? [
  //         { scalabilityMode: 'L3T3', dtx: true },
  //         { scalabilityMode: 'L2T3', dtx: true, scaleResolutionDownBy: 2 },
  //         { scalabilityMode: 'L1T3', dtx: true, scaleResolutionDownBy: 4 }
  //       ] : undefined,
  //       codecOptions: isVideo.current ? {
  //         videoGoogleStartBitrate: 1000
  //       } : {
  //         opusStereo: true,
  //         opusDtx: true
  //       }
  //     });


  // // 5. Create server-side consumer and get parameters
  // console.log('Creating server consumer...');
  // const consumerData = await socket.emitWithAck('createConsumer', {
  //   producerId: fromProducer,
  //   rtpCapabilities: mediasoupDevice.current.rtpCapabilities,
  //   socketId: fromSocket
  // });


  //  // 6. Create local consumer
  //  console.log('Creating local consumer...');
  //  const consumer = await recvTransport.current.consume({
  //    id: consumerData.id,
  //    producerId: consumerData.producerId,
  //    kind: consumerData.kind,
  //    rtpParameters: consumerData.rtpParameters,
  //    paused: false
  //  });

  //  await socket.emitWithAck('resume', {
  //   producerId: fromProducer,
  //   //socketId: data.fromSocket
  // });

     // Produce local AUDIO track
    const audioTrack = newStream.getAudioTracks()[0];
    producers.current.audio = await sendTransport.current.produce({
      track: audioTrack,
      codecOptions: {
        opusStereo: true,
        opusDtx: true
      }
    });

       // Produce local VIDEO track only for video calls
    if (isVideo.current) {
      const videoTrack = newStream.getVideoTracks()[0];
      producers.current.video = await sendTransport.current.produce({
        track: videoTrack,
        encodings: [
          { scalabilityMode: 'L3T3', dtx: true },
          { scalabilityMode: 'L2T3', dtx: true, scaleResolutionDownBy: 2 },
          { scalabilityMode: 'L1T3', dtx: true, scaleResolutionDownBy: 4 }
        ],
        codecOptions: {
          videoGoogleStartBitrate: 1000
        }
      });
    }

       // Create consumers for remote tracks
    const remoteStream = new MediaStream();
    // Consume remote AUDIO
    const audioConsumerData = await socket.emitWithAck('createConsumer', {
      //producerId: fromProducer.audioProducerId,
      producerId: producerInfo.audioProducerId,
      rtpCapabilities: mediasoupDevice.current.rtpCapabilities,
      //socketId: fromSocket
      socketId: targetSocket
    });

    const audioConsumer = await recvTransport.current.consume({
      id: audioConsumerData.id,
      producerId: audioConsumerData.producerId,
      kind: audioConsumerData.kind,
      rtpParameters: audioConsumerData.rtpParameters,
      paused: false
    });

    // await socket.emitWithAck('resume', {
    //   producerId: fromProducer.audioProducerId,
    //   socketId: fromSocket
    // });

    await socket.emitWithAck('resume', {
        producerId: producerInfo.audioProducerId,
        socketId: targetSocket
    });

    audioConsumer.on('transportclose', () => closeCall());
    audioConsumer.on('trackended', () => closeCall());
    consumers.current.set(audioConsumer.id, audioConsumer);
    remoteStream.addTrack(audioConsumer.track);

    // Consume remote VIDEO if it exists (video call)
    if (producerInfo.videoProducerId) {
      const videoConsumerData = await socket.emitWithAck('createConsumer', {
        producerId: producerInfo.videoProducerId,
        rtpCapabilities: mediasoupDevice.current.rtpCapabilities,
        socketId: targetSocket
      });

      const videoConsumer = await recvTransport.current.consume({
        id: videoConsumerData.id,
        producerId: videoConsumerData.producerId,
        kind: videoConsumerData.kind,
        rtpParameters: videoConsumerData.rtpParameters,
        paused: false
      });

      await socket.emitWithAck('resume', {
        producerId: producerInfo.videoProducerId,
        socketId: targetSocket
      });
          videoConsumer.on('transportclose', () => closeCall());
      videoConsumer.on('trackended', () => closeCall());
      consumers.current.set(videoConsumer.id, videoConsumer);
      remoteStream.addTrack(videoConsumer.track);
  }
      // // Handle consumer events
      // consumer.on('transportclose', () => {
      //   console.log('Consumer transport closed');
      //   closeCall();
      // });
  
      // consumer.on('trackended', () => {
      //   console.log('Consumer track ended');
      //   closeCall();
      // });
  


      //   //consumers.current.set(fromProducer, consumer);
      //   consumers.current.set(consumer.id, consumer);
      //   const stream = new MediaStream();
      //   stream.addTrack(consumer.track);
      //   setRemoteStream(stream);
      //   remoteStreamRef.current = stream;

       // Add media stream monitoring


    setRemoteStream(remoteStream);
    remoteStreamRef.current = remoteStream;





        //setRemoteStream(new MediaStream([consumer.track]));
       // 6. Confirm answer with server
       console.log('Sending answer confirmation...');
       // Send answer with both producer IDs
    const answer = await socket.emitWithAck("answerCall", {
      to: targetSocket,
      audioProducerId: producers.current.audio.id,
      videoProducerId: producers.current.video?.id,
    });
       console.log("call answered",answer);
        console.log('Call answered successfully');
        setIsCallModal(false);
        navigate("/messenger/videocall", { replace: true });
        //navigate("/messenger/videocall");
    
      } catch (error) {
        console.error('Answer call error:', error);
        toast.error(error.message || 'Failed to answer call');
        setIsCallModal(false);
        closeCall();
      }
    //}, [createTransport, fromSocket, navigate, socket, initializeDevice, closeCall, fromProducer]);
    }, [createTransport,  navigate, socket, initializeDevice, closeCall]);



  // Decline call
  const declineCall = useCallback(() => {
    socket.emit('declineCall', { to: fromSocketRef.current });
    setIsCallModal(false);
    localStream?.getTracks().forEach(track => track.stop());
    setLocalStream(null);
    fromSocketRef.current = null;
    fromProducerRef.current = null;
  //}, [socket, fromSocket, localStream]);
  }, [socket]);
  // Media controls
  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
    localStream?.getAudioTracks().forEach(track => {
      track.enabled = !track.enabled;
    });
  }, [localStream]);

  // const startVideo = useCallback(() => {
  //   if (localStream) {
  //     const videoTracks = localStream.getVideoTracks();
  //     videoTracks.forEach(track => {
  //       track.enabled = true;
  //       setIsVideoTrack(true);
  //     });
  //   }
  // }, []);

  // const stopVideo = useCallback(() => {
  //   if (localStream) {
  //     const videoTracks = localStream.getVideoTracks();
  //     videoTracks.forEach(track => {
  //       track.enabled = false;
  //       setIsVideoTrack(false);
  //     });
  //   }
  // }, []);

  const startVideo = useCallback(() => {
    const stream = localStreamRef.current;
    if (stream) {
      const videoTracks = stream.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = true;
        setIsVideoTrack(true);
      });
    }
  }, []); 
  
  const stopVideo = useCallback(() => {
    const stream = localStreamRef.current;
    if (stream) {
      const videoTracks = stream.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = false;
        setIsVideoTrack(false);
      });
    }
  }, []);

  // Socket listeners
  useEffect(() => {
    // const handleCallEnded = () => {
    //   if (!isCallActive.current) return; // Only handle if call is active
    //   console.log('Remote ended call');
    //   closeCall();
    //   toast.info('Call ended by the other user');
    // };
    const handleRemoteCallEnd = () => {
      if (!isCallActive.current) return;
      closeCall();
      toast.info('Call ended by the other user');
    };
    const safeHandleIncomingCall = (data) => {
      if (isCallActive.current) return; // Prevent new calls while in call
      handleIncomingCall(data);
    };
  
    socket?.on('callUser', safeHandleIncomingCall);
    //socket?.on('callEnded', handleCallEnded);
      // Correct event name
  socket?.on('callEnded', handleRemoteCallEnd);

    return () => {
     socket?.off('callUser', safeHandleIncomingCall);
     //socket?.off('callEnded', handleCallEnded);
     socket?.off('callEnded', handleRemoteCallEnd);
  };
  }, [socket, closeCall, handleIncomingCall]);


  const contextValue = useMemo(() => ({
    remoteUser, // Now comes from state
    localStream: localStreamRef.current, // From ref
    remoteStream: remoteStreamRef.current, // From ref
    isMuted,
    toggleMute,
    closeCall,
    callUser,
    declineCall,
    answerCall,
    isVideo: isVideo.current,
    isVideoTrack,
    startVideo, 
    stopVideo    

  }), [
    remoteUser,
    localStream,
    remoteStream,
    isMuted,
    closeCall,
    callUser,
    declineCall,
    answerCall,
    isVideo.current,
    isVideoTrack,
    startVideo,  
    stopVideo    
  ]);



  return (
 
    <CallContext.Provider value={contextValue}>

      {isCallModal && (
        <CallModal
          isOpen={isCallModal}
          toggleCallModal={() => setIsCallModal(prev => !prev)}
          isVideo={isVideo.current}
          //user={remoteUser?.current}
          user={remoteUser}
          declineCall={declineCall}
          answerCall={answerCall}
          inComming={isIncomingCall}
        />
      )}
      {children}
    </CallContext.Provider>
  );
}

export default CallProvider;
